import { useEffect, useState, useContext, useRef } from "react";
import axios from "axios";

import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
} from "@mui/material";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { ReloadContext } from "../../context/ReloadContext";
import { getJackContentDownload, getJackContent } from "../../api/jackContent";
import { JACK_UPLOAD_STATUS } from "../../const/index";
import * as MESSAGE from "../../const/message";
import LoadingButton from '@mui/lab/LoadingButton';

type Props = {
  jackContentId: number;
  contentFilePath: string;
  contentFileMei: string;
};

/**
 * ダウンロード確認ダイアログコンポーネント
 *
 * @param {Props} { jackContentId, contentFilePath, contentFileMei }
 * @return {*} 
 */
const JackDownloadConfirmDialog = ({
  jackContentId,
  contentFilePath,
  contentFileMei,
}: Props) => {
  const reloadContext = useContext(ReloadContext);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState(0);
  const [selectedContentFilePath, setSelectedContentFilePath] = useState("");
  const [selectedContentFileMei, setSelectedContentFileMei] = useState("");
  const [open, setOpen] = useState(false);
  // 処理中フラグ（ボタン連打対策）
  const processing = useRef(false);
  const [load, setLoad] = useState<boolean>(false);

  useEffect(() => {
    setIsDownloading(false);
  }, [])

  const handleClickOpen = async (contentId: number, contentFilePath: string, contentFileMei: string) => {
    if (!contentFilePath) {
      return reloadContext?.setSnackbarInfo({
        isOpen: true,
        type: "warning",
        message: "コンテンツがアップロードされていません。",
      });
    };
    const content = (await getJackContent(contentId)).data[0];

    if (content.upload_status === JACK_UPLOAD_STATUS.UPLOADING) {
      return reloadContext?.setSnackbarInfo({
        isOpen: true,
        type: "warning",
        message: MESSAGE.WAIT_FOR_UPLOADING,
      });
    };
    if (content.upload_status === JACK_UPLOAD_STATUS.UPLOAD_FAILED) {
      return reloadContext?.setSnackbarInfo({
        isOpen: true,
        type: "error",
        message: "アップロードに失敗したため、ダウンロードできません。\n再度アップロードしてください。",
      });
    };
    setSelectedContentId(contentId);
    setSelectedContentFilePath(contentFilePath);
    setSelectedContentFileMei(contentFileMei);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickDownload = async () => {
    // 処理中(true)なら非同期処理せずに抜ける
    if (processing.current) return;
    // 処理中フラグを上げる
    processing.current = true;
    // 疑似非同期処理
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 1000);

    setIsDownloading(true);
    setLoad(true);
    let result;
    try {
      // キャンセルトークン生成
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      // ダウンロード中state更新
      reloadContext?.setDownloadingInfo({ isDownloading: true, source });
      result = await getJackContentDownload(source, selectedContentId, selectedContentFilePath);
    } catch (error) {
      handleClose();
      setLoad(false);
      return reloadContext?.setSnackbarInfo({
        isOpen: true,
        type: "error",
        message: MESSAGE.SYSTEM_ERROR,
      });
    } finally {
      setIsDownloading(false);
      // ダウンロード中フラグOFF
      reloadContext?.setDownloadingInfo({ isDownloading: false });
    }

    // ブラウザで同一オリジン以外から直接ダウンロードできない問題があるため、一度S3からダウンロード後、
    // URLを作成しなおして、そちらからブラウザのダウンロードをさせる
    const resultS3Download = await axios.get(result.data.data, {
      responseType: "blob",
    })

    const downloadUrl = URL.createObjectURL(resultS3Download.data);

    // ダウンロード処理
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = selectedContentFileMei;
    a.click();
    a.remove()
    reloadContext?.setReload(reloadContext?.reload + 1);
    setIsDownloading(false);
    setLoad(false);
    handleClose();
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        color="inherit"
      >
        <CloudDownloadIcon
          color="primary"
          fontSize="large"
          onClick={() => handleClickOpen(jackContentId, contentFilePath, contentFileMei)} />
      </IconButton>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            width: "20vw",
          }
        }}
      >
        <Typography component="h3" variant="subtitle1" mt={2} ml={2}>
          ダウンロード確認
        </Typography>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            コンテンツファイルをダウンロードしてもよろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>閉じる</Button>
          <LoadingButton loading={load} variant="contained" onClick={handleClickDownload} disabled={isDownloading}>ダウンロードする</LoadingButton>
        </DialogActions>
      </Dialog>
    </Box >
  );
}

export default JackDownloadConfirmDialog;
